// Tasks as tracked work: statuses, views, the task page, the comment thread, project task settings.
const workEn = {
  apps: {
    title: "Apps",
    intro: "Each app has its own versions. A version can come from a person, a git tag or a deployment.",
    empty: "No apps yet. Add one to start tracking its versions.",
    add: "Add app",
    edit: "Edit app",
    name: "Name",
    required: "Required",
    platformsLabel: "Platforms",
    platformsRequired: "Choose at least one platform",
    platforms: { android: "Android", ios: "iOS", desktop: "Desktop", web: "Web", backend: "Backend" },
    source: "Versions come from",
    sources: { manual: "Added by hand", tag: "Git tags", deployment: "Deployments" },
    sourceHints: {
      manual: "People add versions by hand.",
      tag: "A tag with the prefix and a version number creates that version.",
      deployment: "A deployment to the environment that adds merged changes creates a version.",
    },
    sourceHintEdit: "Changing this only affects new versions.",
    repo: "Repo",
    pickRepo: "Choose a repo",
    removedRepoOption: "{{path}} (removed from this project)",
    noRepos: "This project has no repos yet, so this source can't be used.",
    openGitlab: "Open GitLab settings",
    tagPrefix: "Tag prefix",
    tagPrefixHint: "Such as v or android/. Leave it empty if tags are just the version number.",
    tagMeansReleased: "A tag means the version is released",
    tagReleases: "A tag marks the version as released.",
    tagNotReleases: "A tag doesn't mark the version as released.",
    tagPattern: "tags {{prefix}}*",
    environment: "Production environment",
    environmentHint: "Choose one seen on this project's repos, or type a name. It must match exactly.",
    sharedPrefix: "Shares this repo and tag prefix with {{apps}}, so they will receive the same contents.",
    sharedEnvironment: "Shares this repo and environment with {{apps}}, so they will receive the same contents.",
    versionCount_one: "{{count}} version",
    versionCount_few: "{{count}} versions",
    versionCount_many: "{{count}} versions",
    versionCount_other: "{{count}} versions",
    needsSorting: "Needs sorting",
    needsSortingHint: "The upgrade couldn't tell which app these versions belong to. Check them, then mark this app as sorted.",
    markSorted: "Mark as sorted",
    noPlatforms: "Choose this app's platforms. The upgrade couldn't tell what they are.",
    repoRemoved: "This repo was removed from the project, so the app gets nothing from it. Add the repo again or choose another.",
    archived: "Archived",
    archive: "Archive",
    unarchive: "Unarchive",
    archiveTitle: "Archive {{name}}?",
    archiveBody: "It stops following GitLab and leaves pickers. Its versions stay, and bugs can still be reported on them.",
    archivedToast: "App archived",
    unarchived: "App unarchived",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "It has no versions or sightings, so nothing else is lost.",
    merge: "Merge into another app",
    mergeTitle: "Merge {{name}} into another app?",
    mergeBody: "The app you pick takes all of its versions, sightings, flags, test cases and queued GitLab work, along with its platforms. Versions with the same name become one. {{name}} then goes away, and this can't be undone.",
    mergeInto: "Merge into",
    mergeConfirm: "Merge apps",
    merged: "Apps merged",
    deleted: "App deleted",
    created: "App added",
    saved: "App saved",
    ignoredTags_one: "{{count}} ignored tag",
    ignoredTags_few: "{{count}} ignored tags",
    ignoredTags_many: "{{count}} ignored tags",
    ignoredTags_other: "{{count}} ignored tags",
    ignoredReason: {
      empty: "no version after the prefix",
      not_semver: "not a SemVer version, such as 2.4.0 or 2.4.0-rc.1",
      too_long: "the version is longer than 80 characters",
    },
  },
  gitlab: {
    chip: {
      failed_pipeline: "Pipeline failed",
      failed_pipelineMany: "Pipeline failed · {{count}} MRs",
      changed_since_tested: "Changed since tested",
      changed_since_testedMany: "Changed since tested · {{count}} MRs",
      running_pipeline: "Pipeline running",
      running_pipelineMany: "Pipeline running · {{count}} MRs",
      draft: "Draft MR",
      draftMany: "Draft · {{count}} MRs",
      open: "MR open",
      openMany: "Open · {{count}} MRs",
      merged: "MR merged",
      mergedMany: "Merged · {{count}} MRs",
      deployed: "Deployed",
      deployedMany: "Deployed · {{count}} MRs",
      branch_only: "Branch",
      branch_onlyMany: "{{count}} branches",
    },
    chipHint: {
      failed_pipeline: "A pipeline failed on a linked merge request.",
      changed_since_tested: "New commits arrived after the last finished round. The round's results and the task's status stay as they are.",
      running_pipeline: "A pipeline is running on a linked merge request.",
      draft: "A linked merge request is still a draft.",
      open: "A linked merge request is open.",
      merged: "The linked merge request is merged, and not deployed yet.",
      deployed: "The linked change is merged and deployed.",
      branch_only: "A branch is linked, with no merge request yet.",
      mrs: "Merge requests: {{count}}.",
      branches: "Branches: {{count}}.",
      moreBranches: "Other branches: {{count}}.",
      environments: "Environments: {{environments}}.",
      disconnected: "GitLab is disconnected, so this may be out of date.",
    },
    pipeline: {
      created: "Pipeline created",
      waiting_for_resource: "Pipeline waiting",
      preparing: "Pipeline preparing",
      pending: "Pipeline pending",
      running: "Pipeline running",
      success: "Pipeline passed",
      failed: "Pipeline failed",
      canceling: "Pipeline canceling",
      canceled: "Pipeline canceled",
      skipped: "Pipeline skipped",
      manual: "Pipeline needs a manual step",
      scheduled: "Pipeline scheduled",
      lastKnown: "Last known: {{status}}",
    },
    mrState: { opened: "Open", merged: "Merged", closed: "Closed", locked: "Locked" },
    draft: "Draft",
    source: {
      branch: "Found in the branch name",
      title: "Found in the MR title",
      description: "Found in the MR description",
      manual: "Added by hand",
    },
    mergeRequests: "Merge requests",
    branches: "Branches",
    environments: "Environments",
    disconnected: "GitLab is disconnected. This is what Mixla last saw, {{time}}. Links can't be added until a platform manager reconnects it.",
    disconnectedNever: "GitLab is disconnected. Links can't be added until a platform manager reconnects it.",
    changedNotice: "New commits arrived after the last finished round. The round's results and the task's status stay as they are, and a retest is up to you or an enabled rule.",
    waiting: "Waiting to move to {{status}}: {{blockers}}",
    blockerEnvironment: "{{mr}} not yet in {{environment}}",
    blockerMerge: "{{mr}} not merged yet",
    emptyTitle: "No code linked yet",
    emptyBody: "Push a branch with this name, or put the task's key in a merge request's title, and it links on its own. Older work appears once GitLab next reports activity on it.",
    copyBranch: "Copy branch name",
    branchForNewWork: "Branch name for new work",
    addLink: "Link a merge request or branch",
    addLinkPlaceholder: "Paste its GitLab URL",
    addLinkButton: "Add link",
    addLinkHint: "It must come from a repo that feeds this project.",
    linkAdded: "Link added",
    lastSynced: "Last synced {{time}}",
    bugEmptyTitle: "No code linked yet",
    bugEmptyBody: "Push a branch with this name, or put the bug's key in a merge request's title, and it links on its own.",
    bugUnconfirmed: "Only mentioned in the description. It doesn't count as shipping a fix, or towards marking the bug fixed, until someone confirms it.",
    unconfirmed: "Only mentioned in the description. It doesn't count for rules, round comments or the card until someone confirms it.",
    confirmLink: "Confirm link",
    linkConfirmed: "Link confirmed",
    removeLink: "Remove link",
    removeTitle: "Remove the link to {{name}}?",
    removeBody: "It stays removed, even when GitLab reports new activity on it. You can add it back by hand.",
    linkRemoved: "Link removed",
  },
  testStageDetail: {
    round: "round {{n}}",
    failed_one: "{{count}} failed",
    failed_few: "{{count}} failed",
    failed: "{{count}} failed",
  },
  doneGuard: {
    title: "Move untested work to Done?",
    one: "{{key}} hasn't passed testing. It will be marked “not verified”.",
    many_one: "{{keys}} hasn't passed testing ({{count}} of {{total}} tasks). It will be marked “not verified”.",
    many_few: "{{keys}} haven't passed testing ({{count}} of {{total}} tasks). They will be marked “not verified”.",
    many: "{{keys}} haven't passed testing ({{count}} of {{total}} tasks). They will be marked “not verified”.",
    confirm: "Move to Done",
  },
  testStage: {
    not_started: "Not tested",
    ready_for_test: "Ready for test",
    in_testing: "In testing",
    changes_requested: "Changes requested",
    ready_for_retest: "Ready for retest",
    passed: "Passed",
  },
  poker: {
    nav: "Planning poker",
    title: "Planning poker",
    start: "Planning poker",
    banner: "Planning poker is on — {{name}} facilitates ({{done}} / {{total}} done)",
    join: "Join",
    noneTitle: "No planning poker running",
    noneBody: "Start one from a sprint on the Backlog page: it estimates the sprint's unestimated tasks together.",
    present_one: "{{count}} here",
    present_few: "{{count}} here",
    present: "{{count}} here",
    facilitator: "Facilitator: {{name}}",
    takeOver: "Take over",
    end: "End session",
    round: "Round {{n}}",
    yourVote: "Your vote",
    changeUntilReveal: "You can change it until the votes are revealed.",
    spread: "Spread {{spread}}",
    nVotes_one: "{{count}} vote",
    nVotes_few: "{{count}} votes",
    nVotes: "{{count}} votes",
    noVotes: "no votes",
    agree: "Agreed estimate:",
    reveal: "Reveal votes",
    revote: "Vote again",
    skip: "Skip",
    skipped: "skipped",
    allDone: "Every task has an outcome",
    allDoneBody: "Pick a skipped one in the queue, or end the session.",
    votes: "Votes · {{voted}} of {{total}}",
    votedLabel: "{{name}} has voted",
    notVotedLabel: "{{name}} hasn't voted",
    queue: "Tasks · {{done}} / {{total}}",
    saidRevealed: "Votes revealed",
    saidVoted: "{{n}} of {{total}} voted",
    saidFacilitator: "{{name}} facilitates now",
  },
  templates: {
    saveAs: "Save as template",
    saveAsHint: "Its title, description, priority, tags, estimate, checklists (unticked) and subtask titles.",
    saved: "Template saved",
    name: "Template name",
    taskTitle: "Task title",
    fromTemplate: "Template",
    blank: "Blank task",
    fromNamed: "From “{{name}}”",
    titleHint: "Leave empty to use “{{title}}”.",
    checklistFromTemplate: "Checklist from template",
    taskTemplates: "Task templates",
    taskTemplatesIntro: "Tasks you create again and again: title, description, priority, estimate, checklists and subtasks. Pick one in “New task”.",
    new: "New template",
    edit: "Edit template",
    none: "No templates yet. Make one here, or from any task's ··· menu.",
    nChecklists_one: "{{count}} checklist",
    nChecklists_few: "{{count}} checklists",
    nChecklists: "{{count}} checklists",
    nSubtasks_one: "{{count}} subtask",
    nSubtasks_few: "{{count}} subtasks",
    nSubtasks: "{{count}} subtasks",
    checklists: "Checklists",
    checklistsHint: "One checklist per block: its name on the first line, items below. Separate checklists with a blank line.",
    subtasks: "Subtasks",
    subtasksHint: "One subtask title per line.",
    deleteTitle: "Delete the template “{{name}}”?",
    deleteBody: "Tasks made from it stay. Recurring rules using it are deleted too.",
    checklistTemplates: "Checklist templates",
    checklistTemplatesIntro: "Checklists to add to any task in one click.",
    newChecklist: "New checklist template",
    editChecklist: "Edit checklist template",
    noneChecklists: "No checklist templates yet.",
    items: "Items",
    itemsHint: "One item per line.",
    nItems_one: "{{count}} item",
    nItems_few: "{{count}} items",
    nItems: "{{count}} items",
    recurring: "Recurring tasks",
    recurringIntro: "A template made into a task on a schedule, in the project's timezone. Recurring tasks put in the active sprint don't count as sprint scope.",
    newRule: "New recurring task",
    editRule: "Edit recurring task",
    needTemplate: "Make a task template first.",
    noRules: "No recurring tasks yet.",
    template: "Template",
    repeat: "Repeat",
    on: "On",
    onDays: "On these days",
    dayOfMonth: "Day of the month",
    hour: "Hour",
    atHour: "at {{hour}}:00",
    intoSprint: "into the active sprint",
    intoSprintLabel: "Put it in the active sprint (the backlog when none is active)",
    skipIfOpen: "Skip a run while the last one it made is still open",
    ruleHint: "Times are in the project's timezone (Sprint settings).",
    pause: "Pause",
    resume: "Resume",
    paused: "Paused",
    pausedBecause: "Paused: {{reason}}",
    nextRun: "Next: {{when}}",
    lastTask: "last made {{key}}",
    rule: {
      daily: "Every day",
      dailyOn: "Every {{days}}",
      weekly: "Every {{day}}",
      monthly: "Monthly on day {{day}}",
      kindDaily: "Daily",
      kindWeekly: "Weekly",
      kindMonthly: "Monthly",
    },
  },
  flow: {
    nav: "Flow",
    title: "Flow",
    period: "Period",
    lastDays: "Last {{n}} days",
    days: "{{n}} d",
    historySince: "Status history is recorded since {{date}}; work before that isn't measured.",
    notEnough: "{{n}} finished tasks with a measured cycle time so far: percentiles and the aging flag show from {{min}}.",
    cycleMedian: "Cycle time (median)",
    p85: "85% within {{v}}",
    leadMedian: "Lead time (median)",
    throughput: "Throughput",
    perWeek: "{{n}} / week",
    completed_one: "{{count}} finished",
    completed_few: "{{count}} finished",
    completed: "{{count}} finished",
    wip: "In progress now",
    wipHint: "Tasks in an active status",
    cfd: "Cumulative flow",
    cfdHint: "Tasks per status group at each day's end. A widening middle band means work is piling up in progress.",
    cat: { not_started: "Not started", active: "In progress", done: "Done" },
    aging: "Aging work",
    agingHint: "Open tasks by time in their current status. Flagged: longer than {{v}} (85% of finished work took less).",
    agingHintNoData: "Open tasks by time in their current status.",
    overP85: "longer than usual",
    noAging: "Nothing in progress.",
  },
  standup: {
    nav: "Standup",
    title: "Standup",
    since: "since {{date}}",
    range: "Period",
    sinceLastDay: "Since the last working day",
    lastWeek: "Last 7 days",
    scope: "Tasks",
    auto: "Active sprint (or all)",
    sprintScope: "{{name}}",
    allTasks: "All project tasks",
    blocked_one: "{{count}} task blocked",
    blocked_few: "{{count}} tasks blocked",
    blocked: "{{count}} tasks blocked",
    blockedBy: "waiting on {{keys}}",
    finished: "Finished",
    moved: "Moved",
    tested: "Tested",
    round: "round {{n}}: {{passed}} passed, {{failed}} failed",
    now: "Now",
    emptyTitle: "Nothing yet",
    emptyBody: "Moves, comments, test rounds and work in progress show up here.",
  },
  epics: {
    nav: "Epics",
    title: "Epics",
    epic: "Epic",
    new: "New epic",
    edit: "Edit epic",
    name: "Name",
    description: "Description",
    color: "Colour",
    targetDate: "Target date",
    target: "Target {{date}}",
    stateLabel: "Epic state",
    state: { planned: "Planned", in_progress: "In progress", done: "Done" },
    noEpic: "No epic",
    none: "No epics yet",
    change: "Change epic",
    followsParent: "A subtask is in its parent's epic",
    noTasks: "No tasks yet",
    progressTasks: "{{done}} / {{total}} tasks",
    progressPts: "{{done}} / {{total}} pts",
    doneEpics: "Done",
    emptyTitle: "No epics yet",
    emptyBody: "An epic groups tasks across sprints, with a progress bar. Tasks pick an epic like they pick a sprint.",
    deleteTitle: "Delete the epic “{{name}}”?",
    deleteBody: "Its tasks stay, without an epic.",
    filter: "Epic",
    nEpics_one: "{{count}} epic",
    nEpics_few: "{{count}} epics",
    nEpics: "{{count}} epics",
  },
  releases: {
    nav: "Releases",
    title: "Releases",
    release: "Release",
    new: "New release",
    name: "Name",
    nameHint: "SemVer, such as 2.4.0 or 2.4.0-rc.1. A +build part is dropped.",
    targetDate: "Target date",
    state: { unreleased: "Unreleased", released: "Released", archived: "Archived" },
    noRelease: "No release",
    change: "Change release",
    releasedOn: "Released {{date}}",
    targetOn: "Target {{date}}",
    noItems: "Nothing in it yet",
    itemsHint: "Pick this release on tasks (or in bulk) and on fixed bugs.",
    items: "Tasks {{tasks}} · bugs {{bugs}}",
    progressLabel: "{{done}} of {{total}} done and verified",
    nDone_one: "{{count}} done",
    nDone_few: "{{count}} done",
    nDone: "{{count}} done",
    nNotVerified_one: "{{count}} done, not verified",
    nNotVerified_few: "{{count}} done, not verified",
    nNotVerified: "{{count}} done, not verified",
    nOpen_one: "{{count}} open",
    nOpen_few: "{{count}} open",
    nOpen: "{{count}} open",
    nBugsFixed: "{{fixed}} / {{total}} bugs fixed",
    notes: "Release notes",
    notesGenerated: "generated from the items",
    notesEdited: "edited",
    notesFrozen: "frozen at release",
    notesEmpty: "Notes fill in as tasks are done and bugs fixed.",
    editNotes: "Edit notes",
    regenerate: "Back to generated",
    copyMarkdown: "Copy as Markdown",
    copied: "Release notes copied",
    markReleased: "Mark released",
    markTitle: "Release {{name}}?",
    freezeNote: "The release notes freeze as they are now.",
    openItems_one: "{{count}} item is still open.",
    openItems_few: "{{count}} items are still open.",
    openItems: "{{count}} items are still open.",
    moveTo: "Move open items to",
    moveToNew: "Move them to a new release",
    releaseAnyway: "Release anyway (they stay, listed as not included)",
    released: "{{name}} released",
    archive: "Archive",
    unarchive: "Unarchive",
    deleteTitle: "Delete the release “{{name}}”?",
    deleteBody: "Its tasks and bugs stay, without it. If it has sightings, a bug fix or test rounds, it's archived instead.",
    filter: "Release",
    fixRelease: "Fix version",
    app: "App",
    allApps: "All apps",
    chooseApp: "Choose an app",
    showMore: "Show more",
    noAppsTitle: "No apps yet",
    noAppsBody: "Every version belongs to an app, such as Android or Backend. Add one to start.",
    noAppsAsk: "Every version belongs to an app, such as Android or Backend. A project manager can add one.",
    appEmpty: {
      manual: "No versions yet.",
      tag: "No versions yet. They appear when a matching tag is pushed.",
      deployment: "No versions yet. They appear when a production deployment adds merged changes.",
    },
    nameError: {
      empty: "Give the version a name.",
      not_semver: "That isn't a SemVer version. Use a form like 2.4.0 or 2.4.0-rc.1, with no leading v.",
      too_long: "The name is longer than 80 characters.",
    },
    olderHint: "{{app}} gets its versions from deployments, so one added here is an older version that's already out.",
    olderManagerOnly: "{{app}} gets its versions from deployments. Only a manager can add an older one by hand.",
    cameOut: "Date it came out",
    cameOutMissing: "Pick the date it came out.",
    editTarget: "Set target date",
    marker: {
      rolled_back: "Rolled back",
      incomplete: "Contents may be incomplete",
      needs_merge: "Same name as another version",
      notes_frozen: "Notes frozen",
    },
    rolledBackShort: "rolled back",
    rolledBackHint: "Rolled back on {{date}}. Its fixes don't count until this commit, or one containing it, is deployed again.",
    incompleteHint: "Some commits in this version aren't matched to a merge request yet, so items may be missing.",
    incompleteNote: "Items may be missing: {{note}}",
    needsMergeHint: "The upgrade found another version of this name in this app. A manager can merge the two.",
    notesFrozenHint: "Items detected after this point appear in the contents, not in the notes.",
    fromTag: "From tag {{tag}}",
    fromDeployment: "From a deployment",
    group: {
      shipped: "Shipped",
      planned_not_shipped: "Planned, not shipped",
      unplanned: "Shipped without a plan",
    },
    groupEmpty: {
      shipped: "Nothing has shipped yet.",
      planned_not_shipped: "Nothing is waiting: all that was planned has shipped.",
      unplanned: "Nothing shipped without a plan.",
    },
    listCapped: "Showing the first {{listed}} of {{total}} tasks.",
    pickSearch: "Search versions",
    noVersions: "No versions yet",
    noMatch: "No matching versions",
    otherSelected: "Another selected version",
    lock: {
      manager_only: "Already released: only a manager can change it",
      archived: "Archived, so it can't be changed",
    },
    rename: "Rename",
    renameTitle: "Rename {{name}}",
    move: "Move to another app",
    moveTitle: "Move {{name}}",
    moveHint: "Its contents, sightings and flags go with it.",
    moveNoApps: "This project has no other active app.",
    moved: "{{name}} moved to {{app}}",
    merge: "Merge into another version",
    mergeTitle: "Merge {{name}}",
    mergePick: "Pick the version that stays.",
    mergeConfirmTitle: "Merge these versions?",
    mergeConfirm: "Merge into {{name}}",
    mergeBody: "Everything in {{from}} moves to {{to}}, which keeps its name and notes. Links to {{from}} open {{to}}. This can't be undone.",
    merged: "Merged into {{name}}",
    collisionTitle: "{{app}} already has {{name}}",
    collisionBody: "A version can't move to an app that already has its name. You can merge the two instead.",
    nItems_one: "{{count}} item",
    nItems_few: "{{count}} items",
    nItems_many: "{{count}} items",
    nItems_other: "{{count}} items",
    nSightings_one: "{{count}} sighting",
    nSightings_few: "{{count}} sightings",
    nSightings_many: "{{count}} sightings",
    nSightings_other: "{{count}} sightings",
    deletedArchived: "{{name}} has sightings, a bug fix or test rounds, so it was archived instead",
  },
  views: {
    views: "Views",
    mine: "My views",
    shared: "Shared with the project",
    none: "No saved views yet",
    saveAs: "Save as a view…",
    update: "Update “{{name}}”",
    updated: "View updated",
    rename: "Rename view",
    share: "Share with the project",
    unshare: "Make private",
    delete: "Delete view",
    leave: "Close the view",
    moveUp: "Move up",
    moveDown: "Move down",
    unsaved: "Unsaved changes",
    name: "Name",
    shareWithProject: "Share with everyone in the project",
    hint: "A view remembers the filters, view, grouping and sort.",
    deleteTitle: "Delete the view “{{name}}”?",
    deleteBody: "The tasks stay as they are.",
    deleteSharedBody: "It disappears for everyone in the project. The tasks stay as they are.",
    staleFilters: "Some filters in this view no longer exist and were left out.",
  },
  shortcuts: {
    assignedToYou: "Assigned to you",
    title: "Keyboard shortcuts",
    status: "Change status",
    assignees: "Change assignees",
    assignMe: "Assign to me",
    priority: "Change priority",
    estimate: "Change estimate",
    due: "Change due date",
    tags: "Add or remove tags",
    sprint: "Move to a sprint",
    comment: "Write a comment",
    nextPrev: "Next / previous task in the list",
    close: "Close",
    help: "Show this list",
    enabled: "Single-key shortcuts",
    enabledHint: "Turn off if they get in the way of a screen reader or voice control. J, K and Esc keep working.",
  },
  bulk: {
    bar: "Selected tasks",
    nSelected: "{{count}} selected",
    working: "Applying…",
    clear: "Clear",
    add: "Add",
    remove: "Remove",
    only: "Only",
    updated_one: "Updated {{count}} task",
    updated_few: "Updated {{count}} tasks",
    updated: "Updated {{count}} tasks",
    failed: "Nothing changed. {{message}}",
    deleteTitle_one: "Delete {{count}} task?",
    deleteTitle_few: "Delete {{count}} tasks?",
    deleteTitle: "Delete {{count}} tasks?",
    deleteBody: "Their subtasks are deleted with them. This can't be undone.",
    oneProjectOnly: "Status, assignees and sprint need tasks from one project",
    select: "Select {{key}}",
    selectAll: "Select all shown",
    failedRow: "Not changed",
  },
  work: {
    fieldFilter: "Field",
    chooseField: "Choose a field",
    clearFieldFilter: "Clear field filter",
    fieldContains: "Contains…",
    fieldCompare: "Comparison",
    after: "After",
    before: "Before",
    fieldIsSet: "Is set",
    fieldIsEmpty: "Is empty",
    yes: "Yes",
    no: "No",
    properties: "Properties",
    createdBy: "Created by {{name}} · {{date}}",
    descriptionHint: "@ to mention. Saves when you click away · Esc discards · ⌘/Ctrl+Enter saves now.",
    editDescription: "Edit description",
    saving: "Saving…",
    saved: "Saved",
    subtask: "Subtask",
    checklist: "Checklist",
    link: "Link",
    file: "File",
    prevTask: "Previous task",
    nextTask: "Next task",
    nOfTotal: "{{n}} of {{total}}",
    openPage: "Open as a page",
    myWork: "My work",
    myWorkIntro: "Open tasks assigned to you, across every project.",
    allTasks: "All tasks",
    allTasksIntro: "Tasks in every project you can open.",
    newTaskShortcut: "New task (C)",
    search: "Search by key or title",
    view: "View",
    views: { list: "List", board: "Board", table: "Table", calendar: "Calendar" },
    status: "Status",
    nStatuses: "Statuses: {{count}}",
    assignees: "Assignees",
    nPeople: "{{count}} people",
    assignedToMe: "Assigned to me",
    mine: "Mine",
    tags: "Tags",
    nTags: "Tags: {{count}}",
    project: "Project",
    due: "Due",
    anyDue: "Any due date",
    dueThisWeek: "Due within a week",
    dueAt: "Due date and time",
    groupBy: "Group by",
    group: { status: "By status", assignee: "By assignee", due: "By due date", priority: "By priority", project: "By project", none: "No grouping" },
    sort: "Sort",
    sorts: { manual: "Manual order", due: "Due date", priority: "Priority", created: "Newest", updated: "Recently updated", title: "Title", key: "Key" },
    showClosed: "Closed",
    showSubtasks: "Subtasks",
    clearFilters: "Clear filters",
    emptyTitle: "No tasks yet",
    emptyBody: "Create the first task. Tasks Claude pushes from the CLI show up here too.",
    emptyAll: "Nothing here yet.",
    noMatch: "No tasks match.",
    noTasksHere: "No tasks",
    capped: "Showing the first 500 tasks. Narrow the filters to see the rest.",
    addTask: "Add task",
    addTaskTo: "Add a task to {{status}}",
    addTaskOn: "Add a task due {{date}}",
    unassigned: "Unassigned",
    noValue: "No value",
    nobody: "Nobody",
    category: { not_started: "To do", active: "In progress", done: "Done", closed: "Closed" },
    priority: "Priority",
    priorities: { urgent: "Urgent", high: "High", medium: "Medium", low: "Low", none: "No priority" },
    priorityIs: "Priority: {{priority}}",
    changePriority: "Priority: {{priority}}. Change",
    nPriorities: "Priorities: {{count}}",
    dueBucket: { overdue: "Overdue", today: "Today", tomorrow: "Tomorrow", week: "This week", later: "Later", none: "No due date" },
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday",
    inAWeek: "In a week",
    clearDue: "No due date",
    noDue: "No due date",
    changeStatus: "Status: {{status}}. Change",
    changeAssignees: "Change assignees",
    changeDue: "Change due date",
    changeTester: "Change tester",
    searchPeople: "Find a person",
    searchTags: "Find or create a tag",
    createTag: "Create “{{name}}”",
    noTags: "No tags yet",
    newTag: "New tag",
    tagName: "Tag name",
    addTag: "Add tag",
    removeTag: "Remove tag {{name}}",
    tagsAdmin: "Tags",
    tagsIntro: "Labels shared by every project. Anyone who edits tasks can create one; deleting a tag removes it from every task.",
    deleteTagTitle: "Delete tag “{{name}}”?",
    deleteTagBody: "It's removed from every task that has it. Each task's history keeps that it was there.",
    tester: "Tester",
    anyTester: "Any tester in the project",
    created: "Created",
    updated: "Updated",
    key: "Key",
    description: "Description",
    addDescription: "No description yet.",
    writeDescription: "Write a description",
    markdownMentions: "Type @ to mention someone.",
    mentionSomeone: "Mention someone",
    subtasks: "Subtasks",
    subtasksDone: "{{done}} of {{total}} subtasks done",
    addSubtask: "Add a subtask",
    checklists: "Checklists",
    checklistDone: "{{done}} of {{total}} checklist items done",
    addChecklist: "Checklist",
    checklistName: "Checklist name",
    noChecklists: "No checklists.",
    addItem: "Add an item…",
    deleteChecklist: "Delete checklist",
    deleteChecklistTitle: "Delete “{{name}}”?",
    deleteChecklistBody: "The checklist and its items are deleted.",
    files: "Files",
    addFile: "Add file",
    noFiles: "No files.",
    removeFile: "Remove {{name}}",
    links: "Links",
    addLink: "Link task",
    noLinks: "No linked tasks.",
    findTask: "Find a task by key or title",
    rel: { blocked_by: "Blocked by", blocks: "Blocks", relates_to: "Relates to" },
    blocksLabel: "Blocks",
    blockedByLabel: "Blocked by",
    relatesTo: "Relates to",
    blockedBy: "Blocked by {{count}} open tasks",
    blockedNotice: "Blocked by {{count}} open task(s):",
    comments: "{{count}} comments",
    fields: "Fields",
    testing: "Testing",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    changeParent: "Change parent task",
    parentTitle: "Parent of {{key}}",
    currentParent: "Parent",
    makeTopLevel: "Make top-level",
    moveProject: "Move to another project",
    moveTitle: "Move {{key}}",
    moveHint: "The task gets a new key in the destination project. Its test cases, rounds and comments come along; custom field values don't.",
    move: "Move",
    moved: "Moved: now {{key}}",
    firstStatus: "The project's first status",
    deleteTask: "Delete task",
    deleteTitle: "Delete {{key}}?",
    deleteBody: "The task disappears from lists and its key stops working. History is kept.",
    deleteBodySubtasks: "The task and its {{count}} subtask(s) disappear from lists. History is kept.",
    deleted: "{{key}} deleted",
    createAnother: "Create another",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    nMore: "+{{count}} more",
    allDated: "Every task has a due date.",
    dragToDate: "Drag a task onto a day to set its due date.",
  },
  thread: {
    nChanges: "{{count}} changes",
    nChangesBy: "{{actor}} made {{count}} changes",
    collapseChanges: "Show fewer",
    hidePanel: "Hide comments",
    title: "Comments",
    showChanges: "Changes",
    empty: "No comments yet. Start the conversation.",
    older: "Show older",
    new: "New",
    system: "Mixla",
    none: "none",
    noDate: "no date",
    deleted: "Comment deleted",
    edited: "edited",
    editComment: "Edit comment",
    delete: "Delete",
    deleteTitle: "Delete this comment?",
    deleteBody: "Everyone will see that a comment was deleted. Its files can no longer be opened.",
    hide: "Hide for me",
    placeholder: "Write a comment… @ mentions someone",
    hint: "⌘/Ctrl+Enter to send · paste or drop files",
    attach: "Attach files",
    send: "Send",
    tooLarge: "{{name}} is over 100 MB",
    readOnly: "Viewers can read comments but not write them.",
    event: {
      changed: "{{actor}} changed the task",
      created: "{{actor}} created the task",
      status_changed: "{{actor}} moved it from {{from}} to {{to}}",
      due_changed: "{{actor}} changed the due date: {{from}} → {{to}}",
      priority_changed: "{{actor}} changed the priority: {{from}} → {{to}}",
      title_changed: "{{actor}} renamed it from “{{from}}”",
      assignee_added: "{{actor}} assigned {{user}}",
      assignee_removed: "{{actor}} unassigned {{user}}",
      tester_changed: "{{actor}} changed the tester: {{from}} → {{to}}",
      parent_changed: "{{actor}} changed the parent: {{from}} → {{to}}",
      epic_changed: "{{actor}} changed the epic: {{from}} → {{to}}",
      release_changed: "{{actor}} changed the release: {{from}} → {{to}}",
      release_added: "{{actor}} added this to {{to}}",
      release_removed: "{{actor}} took this out of {{from}}",
      tag_added: "{{actor}} added the tag {{tag}}",
      tag_removed: "{{actor}} removed the tag {{tag}}",
      attachment_added: "{{actor}} attached {{file}}",
      attachment_removed: "{{actor}} removed {{file}}",
      moved_out: "{{actor}} moved it to another project: {{from_key}} → {{to_key}}",
      moved_in: "{{actor}} moved it here: {{from_key}} → {{to_key}}",
      deleted: "{{actor}} deleted the task",
    },
  },
  testingBoard: {
    nav: "Testing",
    title: "Testing board",
    intro: "Tasks by test stage. Drag a card from To test to Testing, or press Start, to begin a round.",
    project: "Project",
    allProjects: "All projects",
    nothing: "Nothing to test right now. Tasks show up here when a developer moves them into the Test column.",
    columns: { to_test: "To test", testing: "Testing", waiting: "Waiting on developer", passed: "Passed this sprint" },
    empty: {
      to_test: "Nothing waiting for a test.",
      testing: "No round in progress.",
      waiting: "Nothing back with developers.",
      passed: "Nothing passed yet this sprint.",
    },
    passedMore: "Showing the latest {{count}}.",
    cases_one: "{{count}} case",
    cases_few: "{{count}} cases",
    cases: "{{count}} cases",
    start: "Start round",
    startTitle: "Start testing {{key}}",
    noCases: "This task has no test cases yet.",
    writeCases: "Write them on the task",
    nothingToRetest: "Nothing changed or failed since the last round: pick all cases.",
  },
  workSettings: {
    testingFlow: "Testing column",
    testingFlowIntro: "Moving a task into this status asks the testers for a test (or a retest). Moving it out before testing starts withdraws the request. A round where every case passes moves the task on by itself; a failed round leaves it in place.",
    testingStatus: "Testing status",
    testingNone: "None — statuses don't touch testing",
    onPass: "When a round passes, move to",
    onPassNext: "Next status ({{name}})",
    onPassStay: "Stay (no later status)",
    testingSaved: "Testing column saved",
    wip: "WIP",
    wipHint: "Board columns warn when they hold more tasks than this. Moves aren't blocked.",
    wipFor: "WIP limit for {{name}}",
    colorFor: "Colour of {{name}}",
    colorDefault: "Category colour",
    colors: { gray: "Grey", blue: "Blue", indigo: "Indigo", purple: "Purple", pink: "Pink", red: "Red", orange: "Orange", amber: "Amber", green: "Green", teal: "Teal" },
    overLimit: "over limit",
    wipWarning: "{{name}} is over its WIP limit of {{limit}}",
    statuses: "Task statuses",
    statusesIntro: "The steps a task goes through, in order. The category decides what counts as open or finished; a project always keeps one “Done” status.",
    newStatus: "New status",
    category: "Category",
    nTasks: "Tasks: {{count}}",
    deleteStatus: "Delete status “{{name}}”?",
    deleteStatusBody: "No task uses it.",
    moveTasks: "Tasks with this status: {{count}}. Move them to:",
    moveTo: "Move tasks to",
    fields: "Custom fields",
    fieldsIntro: "Extra properties every task in this project can carry. They show on the task, in the table and as filters.",
    addField: "Add field",
    editField: "Edit field",
    fieldName: "Name",
    fieldType: "Type",
    typeFixed: "A field's type can't change.",
    type: { text: "Text", number: "Number", date: "Date", checkbox: "Checkbox", single_select: "Single select", user: "Person" },
    options: "Options",
    addOption: "Add option",
    color: "Colour",
    optionRemoveHint: "Removing an option clears it from the tasks that chose it.",
    deleteField: "Delete field “{{name}}”?",
    deleteFieldBody: "The field and every task's value of it are deleted.",
  },
};

export default workEn;

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type WorkTranslation = Shape<typeof workEn>;
